import React from 'react';
import styled from 'styled-components';
import { Input, DatePicker, Select } from 'antd';
import SearchSelectBox from './SearchSelectBox';
import {
  TAB_TYPE,
  VOC_STATUS_OPTIONS,
  SERVICE_TYPE_OPTIONS,
  SERVICE_STATUS_OPTIONS,
  MESSAGING_CORP_TYPE_OPTIONS,
  AICC_CORP_TYPE_OPTIONS,
  DEFAULT_CORP_TYPE_OPTIONS,
  SERVICE_TYPE
} from '../../constants';
import type { TabType } from '../../types';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Search } = Input;

// ========== Styled Components ==========

const AdminBoardSearchInner = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const AdminBoardSearch = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin: 10px 0;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: #666;
  font-weight: 600;
`;

// ========== Types ==========

interface SearchInputProps {
  onSearchHandler: (text: string) => void;
  value: [Dayjs | null, Dayjs | null] | null;
  onDateChange: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onDateOpenChange: (open: boolean) => void;
  disabledDate: (current: Dayjs) => boolean;
  activeTab: TabType;
  // VOC 탭 전용
  progress?: string;
  onStatusChange?: (val: string) => void;
  // SERVICE 탭 전용
  serviceTypeFilter?: string;
  onServiceTypeChange?: (val: string) => void;
  corpTypeFilter?: string;
  onCorpTypeChange?: (val: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (val: string) => void;
}

// ========== Component ==========

const SearchInput: React.FC<SearchInputProps> = ({
  onSearchHandler,
  value,
  onDateChange,
  onDateOpenChange,
  disabledDate,
  activeTab,
  progress,
  onStatusChange,
  serviceTypeFilter,
  onServiceTypeChange,
  corpTypeFilter,
  onCorpTypeChange,
  statusFilter,
  onStatusFilterChange
}) => {
  const handleInputChange = (text: string) => {
    onSearchHandler(text);
  };

  // 서비스 종류에 따른 법인종류 옵션 반환
  const getCorpTypeOptions = (): Array<{ value: string; label: string }> => {
    if (serviceTypeFilter === SERVICE_TYPE.MESSAGING) {
      return [...MESSAGING_CORP_TYPE_OPTIONS];
    }
    if (serviceTypeFilter === SERVICE_TYPE.AICC) {
      return [...AICC_CORP_TYPE_OPTIONS];
    }
    return [...DEFAULT_CORP_TYPE_OPTIONS];
  };

  const handleServiceTypeChange = (val: string) => {
    onServiceTypeChange?.(val);
    // 서비스 종류 변경 시 법인종류 초기화
    onCorpTypeChange?.('all');
  };

  return (
    <AdminBoardSearchInner>
      <AdminBoardSearch>
        {/* 기간 */}
        <FieldGroup>
          <FieldLabel>기간</FieldLabel>
          <RangePicker
            value={value}
            disabledDate={disabledDate}
            onCalendarChange={onDateChange}
            onOpenChange={onDateOpenChange}
            changeOnBlur
            style={{ height: 32 }}
          />
        </FieldGroup>

        {/* VOC 탭 - 조치 상태 */}
        {activeTab === TAB_TYPE.VOC && (
          <FieldGroup>
            <FieldLabel>조치 상태</FieldLabel>
            <Select
              style={{ width: 140, height: 32 }}
              value={String(progress)}
              onChange={val => onStatusChange?.(String(val))}
              options={[...VOC_STATUS_OPTIONS]}
            />
          </FieldGroup>
        )}

        {/* SERVICE 탭 - 서비스 종류 */}
        {activeTab === TAB_TYPE.SERVICE && (
          <FieldGroup>
            <FieldLabel>서비스 종류</FieldLabel>
            <Select
              style={{ width: 130, height: 32 }}
              value={serviceTypeFilter ?? 'all'}
              onChange={handleServiceTypeChange}
              options={[...SERVICE_TYPE_OPTIONS]}
            />
          </FieldGroup>
        )}

        {/* SERVICE 탭 - 법인종류 */}
        {activeTab === TAB_TYPE.SERVICE && (
          <FieldGroup>
            <FieldLabel>법인종류</FieldLabel>
            <Select
              style={{ width: 150, height: 32 }}
              value={corpTypeFilter ?? 'all'}
              onChange={val => onCorpTypeChange?.(val)}
              options={getCorpTypeOptions()}
            />
          </FieldGroup>
        )}

        {/* SERVICE 탭 - 승인 상태 */}
        {activeTab === TAB_TYPE.SERVICE && (
          <FieldGroup>
            <FieldLabel>승인 상태</FieldLabel>
            <Select
              style={{ width: 100, height: 32 }}
              value={statusFilter ?? 'all'}
              onChange={val => onStatusFilterChange?.(val)}
              options={[...SERVICE_STATUS_OPTIONS]}
            />
          </FieldGroup>
        )}

        {/* 검색 유형 선택 */}
        <SearchSelectBox activeTab={activeTab} />

        {/* 검색 입력 */}
        <div style={{ minWidth: 300 }}>
          <Search
            placeholder="검색어를 입력하세요"
            allowClear
            enterButton="검색"
            onSearch={handleInputChange}
            style={{ height: 32 }}
          />
        </div>
      </AdminBoardSearch>
    </AdminBoardSearchInner>
  );
};

export default SearchInput;
